import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getCookieCsrfHeaders, getIdAccessToken, redirectToIdLogin, refreshIdSessionOnce } from "../auth/idp";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

export const http = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
    timeout: 15000,
});

http.interceptors.request.use((config) => {
    const token = getIdAccessToken();
    config.headers = config.headers ?? {};
    if (token) {
        (config.headers as any)["Authorization"] = `Bearer ${token}`;
    } else {
        delete (config.headers as any)["Authorization"];
    }

    Object.assign(config.headers as any, getCookieCsrfHeaders(config.method));

    return config;
});

http.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;
        const config = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

        if (!status || !config || status !== 401) {
            return Promise.reject(error);
        }

        const url = String(config.url || "");
        if (url.includes("/api/refresh")) {
            redirectToIdLogin();
            return Promise.reject(error);
        }

        if (config._retried) {
            redirectToIdLogin();
            return Promise.reject(error);
        }

        config._retried = true;

        const refreshed = await refreshIdSessionOnce();
        if (!refreshed) {
            redirectToIdLogin();
            return Promise.reject(error);
        }

        return http.request(config);
    }
);

function normalizeAxiosError(e: unknown): never {
    const ax = e as AxiosError<any>;
    const status = ax.response?.status;
    const details = ax.response?.data;

    if (status === 401) {
        redirectToIdLogin();
    }

    const err = new Error(
        `HTTP ${status ?? "?"}${ax.message ? ` — ${ax.message}` : ""}${
            details ? `: ${typeof details === "string" ? details : JSON.stringify(details)}` : ""
        }`
    );
    (err as any).status = status;
    (err as any).details = details;
    throw err;
}

export const api = {
    async get<T>(path: string, params?: Record<string, any>) {
        try {
            const r = await http.get<T>(path, { params });
            return r.data;
        } catch (e) { normalizeAxiosError(e); }
    },
    async post<T>(path: string, body?: any) {
        try {
            const r = await http.post<T>(path, body ?? undefined);
            return r.data;
        } catch (e) { normalizeAxiosError(e); }
    },
    async put<T>(path: string, body?: any) {
        try {
            const r = await http.put<T>(path, body ?? undefined);
            return r.data;
        } catch (e) { normalizeAxiosError(e); }
    },
    async patch<T>(path: string, body?: any) {
        try {
            const r = await http.patch<T>(path, body ?? undefined);
            return r.data;
        } catch (e) { normalizeAxiosError(e); }
    },
    async delete<T>(path: string) {
        try {
            const r = await http.delete<T>(path);
            return r.data;
        } catch (e) { normalizeAxiosError(e); }
    },
    // ⬇️ загрузка файла (multipart/form-data)
    async upload<T = { url: string }>(path: string, file: File, fieldName = "file") {
        try {
            const form = new FormData();
            form.append(fieldName, file);
            const r = await http.post<T>(path, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return r.data;
        } catch (e) { normalizeAxiosError(e); }
    },
};
